/** Shared Port Consumption filter helpers — client/segment options from tracker data. */

import {
  canonicalizePortDatabaseSegment,
  dedupePortDatabaseSegments,
  portDatabaseSegmentDedupKey,
} from './port-database-segments.util';

const USERNAME_CLIENT_FILTERS: Record<string, (item: any) => boolean> = {
  'Eiger Shipping': (item) =>
    item.client &&
    (item.client === 'Northwell' || item.client.toLowerCase() === 'eiger'),
  'Zeronorth ZN': (item) =>
    item.client && item.client.toLowerCase() === 'zeronorth',
  'Elite Shipping': (item) =>
    item.client && item.client.toLowerCase() === 'elite tankship dmcc',
  'Bunge Shipping': (item) =>
    item.client && item.client.toLowerCase() === 'bunge',
  'Alcor Shipping': (item) =>
    item.client?.toLowerCase() === 'alcor chartering dmcc',
  'EMZoil Shipping': (item) =>
    item.client && (item.client.toLowerCase() === 'emz oil' || item.client.toLowerCase() === 'emzoil fzco'),
  'Ethiopian Shipping & Logistics': (item) =>
    item.client?.toLowerCase() === 'ethiopian shipping & logistics',
};

export function normalizeClientKey(name: string): string {
  return name
    ?.toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/tankship|shipping|dmcc|fzco|ltd|llc|pvt|private|limited/gi, '')
    .trim();
}

function toTitleCase(name: string): string {
  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function applyUsernameClientFilter(data: any[], username: string | null): any[] {
  if (!Array.isArray(data)) return [];
  if (username === 'Meraki - Operations') return data;
  const filter = username ? USERNAME_CLIENT_FILTERS[username] : undefined;
  return filter ? data.filter(filter) : data;
}

export function extractClientOptionsFromRecords(records: any[]): string[] {
  const clientMap = new Map<string, string>();

  (records || []).forEach((item) => {
    if (!item?.client) return;

    const originalName = String(item.client).trim();
    const key = normalizeClientKey(originalName);
    const existing = clientMap.get(key);

    const score = (name: string) => {
      let s = 0;
      if (/dmcc|ltd|llc|pvt|private|limited/i.test(name)) s += 5;
      s += name.split(' ').length;
      s += name.length / 100;
      return s;
    };

    if (!existing) {
      clientMap.set(key, toTitleCase(originalName));
      return;
    }

    if (score(originalName) > score(existing)) {
      clientMap.set(key, toTitleCase(originalName));
    }
  });

  return Array.from(clientMap.values()).sort((a, b) => a.localeCompare(b));
}

export function extractSegmentOptionsFromRecords(records: any[]): string[] {
  return dedupePortDatabaseSegments(
    (records || []).map((item) => item?.segment),
  );
}

export function matchSegmentFilter(itemSegment: string, selectedSegment: string): boolean {
  if (!selectedSegment) return true;

  const item = itemSegment?.toLowerCase().trim() || '';
  const selected = selectedSegment.toLowerCase().trim();
  if (!item) {
    return false;
  }
  if (item === selected) {
    return true;
  }

  const itemCanonical = canonicalizePortDatabaseSegment(itemSegment);
  const selectedCanonical = canonicalizePortDatabaseSegment(selectedSegment);
  if (
    itemCanonical &&
    selectedCanonical &&
    portDatabaseSegmentDedupKey(itemCanonical) ===
      portDatabaseSegmentDedupKey(selectedCanonical)
  ) {
    return true;
  }

  // MR ↔ MR2 (and spaced/punctuated variants) must share the same filter bucket.
  const itemKey = portDatabaseSegmentDedupKey(itemSegment);
  const selectedKey = portDatabaseSegmentDedupKey(selectedSegment);
  if (
    itemKey &&
    selectedKey &&
    (itemKey === selectedKey || areEquivalentSegmentKeys(itemKey, selectedKey))
  ) {
    return true;
  }

  const itemTokens = item.split(/[\/\s,()_-]+/).filter(Boolean);
  const parts = selected.split(/[\/\s]+/).filter(Boolean);

  return parts.some((part) => {
    if (!part) {
      return false;
    }
    const partKey = portDatabaseSegmentDedupKey(part);
    // Short codes (MR, LR1) and their aliases (MR↔MR2) match whole tokens.
    if (part.length <= 3 || partKey === 'mr' || partKey === 'mr2') {
      return itemTokens.some((token) => {
        const tokenKey = portDatabaseSegmentDedupKey(token);
        return (
          token === part ||
          tokenKey === partKey ||
          areEquivalentSegmentKeys(tokenKey, partKey)
        );
      });
    }
    return (
      item.includes(part) ||
      itemTokens.some((token) => token === part) ||
      itemTokens.some((token) =>
        areEquivalentSegmentKeys(
          portDatabaseSegmentDedupKey(token),
          partKey,
        ),
      )
    );
  });
}

/** Alias-group equivalence for filter matching (e.g. mr / mr2). */
function areEquivalentSegmentKeys(a: string, b: string): boolean {
  if (!a || !b) {
    return false;
  }
  if (a === b) {
    return true;
  }
  const mrFamily = new Set(['mr', 'mr2', 'mediumrange']);
  if (mrFamily.has(a) && mrFamily.has(b)) {
    return true;
  }
  return (
    portDatabaseSegmentDedupKey(canonicalizePortDatabaseSegment(a)) ===
    portDatabaseSegmentDedupKey(canonicalizePortDatabaseSegment(b))
  );
}

export function matchClientFilter(itemClient: string, selectedClient: string): boolean {
  if (!selectedClient) return true;
  if (!itemClient) return false;

  const itemNorm = normalizeClientKey(itemClient);
  const selectedNorm = normalizeClientKey(selectedClient);

  return (
    itemNorm === selectedNorm ||
    itemClient.trim().toLowerCase() === selectedClient.trim().toLowerCase()
  );
}

export function matchesAnySegment(itemSegment: string, selectedSegments: string[]): boolean {
  if (!selectedSegments?.length) return true;
  return selectedSegments.some((seg) => matchSegmentFilter(itemSegment, seg));
}

/**
 * Map segment picks onto cascade option labels (MR ↔ MR2, etc.).
 * Keeps the user's selection when the API returns a narrowed option list.
 */
export function pruneSegmentSelectionsToOptions(
  selected: string[],
  options: string[],
): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const value of selected || []) {
    const valueKey = portDatabaseSegmentDedupKey(value);
    if (!valueKey || seen.has(valueKey)) {
      continue;
    }

    const canonical =
      (options || []).find((opt) => matchSegmentFilter(opt, value)) ?? value;
    const canonicalKey = portDatabaseSegmentDedupKey(canonical);
    if (!canonicalKey || seen.has(canonicalKey)) {
      continue;
    }

    seen.add(canonicalKey);
    result.push(canonical);
  }

  return result;
}

export function matchesAnyClient(itemClient: string, selectedClients: string[]): boolean {
  if (!selectedClients?.length) return true;
  return selectedClients.some((client) => matchClientFilter(itemClient, client));
}

/** Case-insensitive exact match for multi-select vessel names (API often uppercases). */
export function matchesSelectedVesselNames(
  vesselName: string | null | undefined,
  selectedShips: string[],
): boolean {
  if (!selectedShips?.length) {
    return true;
  }
  const name = String(vesselName ?? '').trim().toUpperCase();
  if (!name) {
    return false;
  }
  return selectedShips.some(
    (ship) => ship.trim().toUpperCase() === name,
  );
}

/** Case-insensitive exact match for multi-select port names. */
export function matchesSelectedPorts(
  port: string | null | undefined,
  selectedPorts: string[],
): boolean {
  if (!selectedPorts?.length) {
    return true;
  }
  const itemPort = String(port ?? '').trim().toLowerCase();
  if (!itemPort) {
    return false;
  }
  return selectedPorts.some(
    (selected) => selected.trim().toLowerCase() === itemPort,
  );
}

/** Case-insensitive exact match for multi-select string values. */
export function matchesSelectedValues(
  value: string | null | undefined,
  selected: string[],
): boolean {
  if (!selected?.length) {
    return true;
  }
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return selected.some(
    (entry) => entry.trim().toLowerCase() === normalized,
  );
}

/** Normalize a filter date to start or end of day for inclusive range checks. */
export function normalizeFilterDate(
  value: string | Date | null | undefined,
  endOfDay = false,
): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    const d = new Date(value.getTime());
    if (endOfDay) {
      d.setUTCHours(23, 59, 59, 999);
    } else {
      d.setUTCHours(0, 0, 0, 0);
    }
    return d;
  }
  const str = String(value).trim();
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    const y = parseInt(ymdMatch[1], 10);
    const m = parseInt(ymdMatch[2], 10) - 1;
    const d = parseInt(ymdMatch[3], 10);
    return new Date(
      Date.UTC(y, m, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0),
    );
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  if (endOfDay) {
    parsed.setUTCHours(23, 59, 59, 999);
  } else {
    parsed.setUTCHours(0, 0, 0, 0);
  }
  return parsed;
}

/** Match a single record date against an optional from/to range. Rows without dates pass. */
export function matchesDateRange(
  itemDateRaw: string | Date | null | undefined,
  fromDateRaw: string | Date | null | undefined,
  toDateRaw: string | Date | null | undefined,
): boolean {
  let fromDate = normalizeFilterDate(fromDateRaw);
  let toDate = normalizeFilterDate(toDateRaw, true);
  if (!fromDate && !toDate) {
    return true;
  }
  if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
    const earlier = normalizeFilterDate(toDateRaw);
    const later = normalizeFilterDate(fromDateRaw, true);
    fromDate = earlier;
    toDate = later;
  }

  if (!itemDateRaw) {
    return true;
  }

  const itemDate = new Date(itemDateRaw);
  if (Number.isNaN(itemDate.getTime())) {
    return true;
  }

  if (fromDate && itemDate < fromDate) {
    return false;
  }
  if (toDate && itemDate > toDate) {
    return false;
  }
  return true;
}

/** Match a port call (arrival/departure) against a date range using overlap logic. */
export function matchesPortCallDateRange(
  arrivalRaw: string | Date | null | undefined,
  departureRaw: string | Date | null | undefined,
  fromDateRaw: string | Date | null | undefined,
  toDateRaw: string | Date | null | undefined,
): boolean {
  let fromDate = normalizeFilterDate(fromDateRaw);
  let toDate = normalizeFilterDate(toDateRaw, true);
  if (!fromDate && !toDate) {
    return true;
  }
  if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
    const earlier = normalizeFilterDate(toDateRaw);
    const later = normalizeFilterDate(fromDateRaw, true);
    fromDate = earlier;
    toDate = later;
  }

  const arrival = arrivalRaw ? new Date(arrivalRaw) : null;
  const departure = departureRaw ? new Date(departureRaw) : null;
  const hasArrival = arrival && !Number.isNaN(arrival.getTime());
  const hasDeparture = departure && !Number.isNaN(departure.getTime());

  if (!hasArrival && !hasDeparture) {
    return !fromDate && !toDate;
  }

  const itemStart = hasArrival ? arrival! : departure!;
  const itemEnd = hasDeparture ? departure! : arrival!;

  if (fromDate && itemEnd < fromDate) {
    return false;
  }
  if (toDate && itemStart > toDate) {
    return false;
  }
  return true;
}

/** Resolve the first available date field from a record. */
export function resolveRecordDate(
  record: Record<string, unknown>,
  fields: string[],
): string | null {
  for (const field of fields) {
    const value = field
      .split('.')
      .reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], record);
    if (value != null && value !== '') {
      return String(value);
    }
  }
  return null;
}
